/** @format */

import React, { useMemo } from "react";
import { toast } from "react-toastify";
import { CreateUpdateStoreModal } from "../../../store-master/components/store-master/create-update-modal";
import { invokeApi, HTTP_METHODS } from "../../utils/http-service";
import { getOfflineData } from "../../utils/offline-services";
import { CF_APPROVAL_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";
import { tokenMainKeys } from "../../../../utils/constants";

// Thin wrapper around the shared CreateUpdateStoreModal for the CF flow: pre-fills the store form
// from the CF record, and on successful creation links the new store id back onto the CF via
// PUT /:id/store-id before bubbling success up to the parent (which reloads the CF).
const CreateStoreForCf = ({ open, onClose, form, financial, onSuccess }) => {
  const currentUser = getOfflineData("user");

  // Map CF + financial fields -> store-master fields (only set keys that are present).
  const createStoreData = useMemo(() => {
    if (!form) return {};
    const fin = financial?.data || {};
    const data = { storeType: "COCO_BUILT", country: "India" };
    if (form.siteName) data.ownerName = form.siteName;
    if (form.storeCode) data.storeNumber = form.storeCode;
    if (form.sequenceCode) data.sequenceNumber = form.sequenceCode;
    if (form.satelliteCode) data.satelliteNumber = form.satelliteCode;
    // REGIONS_LIST values are lowercase ("north"); CF stores them capitalized.
    if (form.address?.region) data.region = String(form.address.region).toLowerCase();
    if (form.address?.circle) data.circle = form.address.circle;
    if (form.address?.city) data.city = form.address.city;
    if (form.address?.pincode) data.pinCode = form.address.pincode;

    // Fields sourced from the CF financial sheet (free-form keys), falling back to CF address.
    data.address = fin["Address"] || form.address?.street || data.address;
    if (fin["State"]) data.state = fin["State"];
    if(fin["Format"]) data.subFormat = fin["Format"];
    if(fin["Pricing Cohort"]) data.priceCohort = fin["Pricing Cohort"] === "National" ? "National Price" : fin["Pricing Cohort"];
    // "Lat Long" is a single "lat, long" string -> split into latitude/longitude.
    if (fin["Lat Long"]) {
      const [lat, lng] = String(fin["Lat Long"]).split(",").map((s) => s.trim());
      if (lat) data.latitude = lat;
      if (lng) data.longitude = lng;
    }
    return data;
  }, [form, financial]);

  // Modal onSuccess receives the created store (createUpdateStore passes the response).
  // Link the new store id back onto the CF via the existing details endpoint.
  const handleStoreCreated = (createdStore) => {
    const newStoreId = createdStore?.id;
    if (!newStoreId) {
      toast.error("Store created but its id could not be read; CF not linked");
      return;
    }
    invokeApi(
      HTTP_METHODS.PUT,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}${form.id}/details`,
      {
        storeId: +newStoreId,
        storeCode: `${createdStore.storeNumber}`,
        sequenceCode: `${createdStore.sequenceNumber}`,
        satelliteCode: `${createdStore.satelliteNumber}`,
        gstNumber: createdStore.gstn,
      },
    )
      .then((res) => {
        if (res?.message) {
          toast.error(res.message);
          return;
        }
        toast.success("Store created and linked to CF");
        onSuccess && onSuccess();
      })
      .catch((err) => toast.error(err?.message || "Failed to link store to CF"));
  };

  return (
    <CreateUpdateStoreModal
      showForm={open}
      createStoreData={createStoreData}
      setShowForm={onClose}
      onSuccess={handleStoreCreated}
      tokenKey={tokenMainKeys.cfApprovalV2}
      role={currentUser?.role?.toUpperCase() === "ADMIN" ? "admin" : undefined}
    />
  );
};

export default CreateStoreForCf;
